import type { FormulaFieldState } from '@skop-app/lib-formula-core'
import { readSkopFormulaFieldValue } from '@skop-app/lib-formula-core'

import type { FormulaEvaluationContext } from '@/types'
import { formulaOverride, getFormulaValue } from '@/utils/formula.helpers'

import {
  calculateMass,
  normaliseMassBasis,
  normalizeLengthUnit,
} from '@skop-app/lib-domain/mass'
import { parseMassFormulaSettings } from './massFormula.config'

type Row = Record<string, unknown>
type Column = { field: string; config: unknown }

const UUID = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i

const scalar = (value: unknown): unknown => {
  if (Array.isArray(value)) return scalar(value[0])
  if (value && typeof value === 'object') {
    const item = value as Record<string, unknown>
    return scalar(item.label ?? item.value ?? item.id)
  }
  return value
}

const text = (value: unknown): string | undefined => {
  const unwrapped = scalar(value)
  if (unwrapped === null || unwrapped === undefined || unwrapped === '') return
  return String(unwrapped).replace(/^"([\s\S]*)"$/, '$1')
}

const numeric = (value: unknown): number | undefined => {
  const valueText = text(value)
  if (!valueText?.trim()) return
  return Number(valueText.replace(',', '.'))
}

const rawReference = (row: Row, key: string): unknown => {
  const property = Array.isArray(row.properties)
    ? row.properties.find((item) => item?.key === key)
    : undefined
  if (property) return scalar(property.values)
  if (
    row.properties &&
    typeof row.properties === 'object' &&
    !Array.isArray(row.properties)
  ) {
    const value = (row.properties as Row)[key]
    if (value !== undefined) return scalar(value)
  }
  if (row[key] !== undefined) return scalar(row[key])
  return key.split('.').reduce<unknown>((value, part) => {
    if (!value || typeof value !== 'object') return undefined
    return (value as Row)[part]
  }, row)
}

const resolveOptionLabel = (
  row: Row,
  key: string,
  context: FormulaEvaluationContext,
  pending?: Set<string>,
): string | undefined => {
  const value = text(rawReference(row, key))
  if (!value) return
  const lookup = context.optionsByID?.[key]
  const option = Object.values(lookup ?? {}).find((item) => {
    if (!item || typeof item !== 'object') return false
    const record = item as Row
    return [record.id, record.value, record.label].some(
      (candidate) => String(candidate) === value,
    )
  }) as Row | undefined
  if (option) {
    const candidates = [text(option.label), text(option.value)]
    return (
      candidates.find((candidate) =>
        key === 'dimension.unit'
          ? normalizeLengthUnit(candidate) !== null
          : normaliseMassBasis(candidate) !== null ||
            (key === 'quantity.unit' &&
              ['kg', 't'].includes(candidate?.toLowerCase() ?? '')),
      ) ?? candidates.find(Boolean)
    )
  }
  if (UUID.test(value)) {
    if (!lookup) pending?.add(key)
    return
  }
  return value
}

/** References supply catalogue data, never occurrence measurements or quantities. */
export const withMassReferenceSources = (
  row: Row,
  reference: Row | undefined,
  settings: ReturnType<typeof parseMassFormulaSettings>,
): Row => {
  if (!reference || !settings) return row
  const properties = Array.isArray(row.properties) ? [...row.properties] : []
  for (const key of [
    'material',
    settings.massUnitField,
    settings.defaultUnitMassField,
    settings.defaultHeightField,
  ]) {
    if (!key || text(rawReference(row, key)) !== undefined) continue
    let value = rawReference(reference, key)
    if (value === undefined && key === settings.defaultUnitMassField) {
      value = formulaOverride(
        text(rawReference(reference, settings.unitMassField)),
      )
    }
    if (value !== undefined) properties.push({ key, values: [scalar(value)] })
  }
  const next: Row = { ...row, properties }
  delete next.__propIndex
  return next
}

export const computeMassFormulaEntries = ({
  row,
  columns,
  context,
}: {
  row: Row
  columns: Column[]
  context: FormulaEvaluationContext
}) => {
  const entries: Array<{
    field: string
    state: FormulaFieldState
    preserveManual: boolean
  }> = []

  for (const { field, config } of columns) {
    const settings = parseMassFormulaSettings(config)
    if (!settings) continue
    const referenceId = text(row.referenceID)
    const reference = referenceId
      ? context.referencesById?.[referenceId]
      : undefined
    const sourceRow = withMassReferenceSources(
      row,
      reference && typeof reference === 'object'
        ? (reference as Row)
        : undefined,
      settings,
    )
    const pending = new Set<string>()
    const optionLabel = (key: string) =>
      resolveOptionLabel(sourceRow, key, context, pending)
    const numberAt = (key?: string) => {
      if (!key) return undefined
      const value = numeric(rawReference(sourceRow, key))
      if (
        value === undefined &&
        key.startsWith('dimension.') &&
        Array.isArray(sourceRow.dimensionRange) &&
        sourceRow.dimensionRange.some(
          (range) => range?.key === key.slice('dimension.'.length),
        )
      )
        return Number.NaN
      return value
    }
    const manual = formulaOverride(
      readSkopFormulaFieldValue(row, settings.unitMassField),
    )
    const material = text(rawReference(sourceRow, 'material'))
    const density = numeric(
      getFormulaValue(
        'material.high_kg/m3',
        {
          ...sourceRow,
          material: rawReference(sourceRow, 'material'),
        },
        context,
      ),
    )
    const result = calculateMass({
      quantity: numberAt('quantity.number'),
      quantityUnit: optionLabel('quantity.unit'),
      massUnit: optionLabel(settings.massUnitField),
      density,
      dimension: {
        length: numberAt('dimension.length'),
        width: numberAt('dimension.width'),
        height: numberAt('dimension.height'),
        volume: numberAt('dimension.volume'),
        unit: optionLabel('dimension.unit'),
      },
      defaultHeight: numberAt(settings.defaultHeightField),
      defaultHeightUnit: 'cm',
      defaultUnitMass: numberAt(settings.defaultUnitMassField),
      manualUnitMass:
        manual === undefined ? undefined : (numeric(manual) ?? Number.NaN),
    })
    const value = result[settings.output]
    if (
      value === null &&
      ['unitMass', 'totalMassKg'].includes(settings.output) &&
      manual === undefined &&
      numberAt(settings.defaultUnitMassField) === undefined &&
      material &&
      UUID.test(material) &&
      !context.optionsByID?.material
    ) {
      pending.add('material')
    }
    const relevantPending = [...pending].filter((reference) => {
      if (settings.output === 'volumeM3') return reference === 'dimension.unit'
      if (settings.output === 'unitMass') return reference !== 'quantity.unit'
      return true
    })
    entries.push({
      field,
      state:
        value !== null
          ? { status: 'valid', value: String(value) }
          : relevantPending.length
            ? { status: 'pending', referenceName: relevantPending[0] }
            : { status: 'missing-inputs', referenceName: field },
      preserveManual: settings.output === 'unitMass' && manual !== undefined,
    })
  }
  return entries
}

/** Aggregate displayed leaf quantities only when their effective units agree. */
export const sumMassConvertedQuantities = ({
  rows,
  field,
  config,
  context,
}: {
  rows: Array<Row | undefined>
  field: string
  config: unknown
  context: FormulaEvaluationContext
}): number | null => {
  const settings = parseMassFormulaSettings(config)
  if (settings?.output !== 'convertedQuantity' || rows.length === 0) return null
  let basis: ReturnType<typeof normaliseMassBasis> = null
  let sum = 0
  for (const row of rows) {
    if (!row) return null
    const raw = text(rawReference(row, field))
    const value = numeric(formulaOverride(raw) ?? raw)
    if (value === undefined || !Number.isFinite(value) || value < 0) return null
    const referenceId = text(row.referenceID)
    const reference = referenceId
      ? context.referencesById?.[referenceId]
      : undefined
    const sourceRow = withMassReferenceSources(
      row,
      reference && typeof reference === 'object'
        ? (reference as Row)
        : undefined,
      settings,
    )
    const rowBasis = normaliseMassBasis(
      resolveOptionLabel(sourceRow, settings.massUnitField, context),
    )
    if (!rowBasis || (basis && rowBasis !== basis)) return null
    basis = rowBasis
    sum += value
    if (!Number.isFinite(sum)) return null
  }
  return sum
}

/** Derived values are available to ordinary dependent formulas in this pass. */
export const withMassFormulaValues = (
  row: Row,
  entries: ReturnType<typeof computeMassFormulaEntries>,
): Row => {
  if (entries.length === 0) return row
  const properties = Array.isArray(row.properties) ? [...row.properties] : []
  for (const { field, state, preserveManual } of entries) {
    if (state.status === 'pending' || preserveManual) continue
    const property = {
      key: field,
      type: 'FORMULA',
      values: state.status === 'valid' ? [String(state.value)] : [],
    }
    const index = properties.findIndex((item) => item?.key === field)
    if (index >= 0) properties[index] = { ...properties[index], ...property }
    else properties.push(property)
  }
  const next: Row = { ...row, properties }
  delete next.__propIndex
  return next
}
